/* eslint-disable react/no-unknown-property */
import { useRef, useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { PALETTE, colorForIndex, TIMELINE } from './palette';

const LETTERS_COUNT = 7;
const SHARDS_PER_LETTER = 16;
const TOTAL_SHARDS = LETTERS_COUNT * SHARDS_PER_LETTER;
const GALAXY_COUNT = 150;
const DUST_COUNT = 280;

function smooth(t) { return t * t * (3 - 2 * t); }
function remap(v, a, b) {
  if (b === a) return 0;
  return Math.min(1, Math.max(0, (v - a) / (b - a)));
}

/**
 * Imperative scene — builds ALL meshes in pure Three.js inside a useEffect.
 * Bypasses R3F JSX reconciler entirely (avoids __source / __self prop crashes).
 * Uses useFrame for per-frame animation.
 */
export default function ShatterScene({ progress, mouse }) {
  const { scene, camera } = useThree();

  // ---------- Build everything ONCE on mount ----------
  const refs = useMemo(() => ({
    shards: null,
    galaxy: null,
    dust: null,
    shardData: [],
    galaxyData: [],
    dustData: [],
    dummy: new THREE.Object3D(),
    lookTarget: new THREE.Vector3(0, 0, 6),
    cameraTarget: new THREE.Vector3(),
  }), []);

  useEffect(() => {
    // Scene styling
    scene.background = new THREE.Color(PALETTE.bg);
    scene.fog = new THREE.Fog(PALETTE.bg, 10, 30);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.4);
    const dir = new THREE.DirectionalLight(PALETTE.primary, 1.4);
    dir.position.set(5, 10, 5);
    const p1 = new THREE.PointLight(PALETTE.secondary, 3, 22);
    p1.position.set(-6, 4, 4);
    const p2 = new THREE.PointLight(PALETTE.accent, 2.5, 20);
    p2.position.set(6, -3, 2);
    scene.add(ambient, dir, p1, p2);

    // --- Shards (InstancedMesh of tetrahedrons) ---
    const shardData = [];
    for (let li = 0; li < LETTERS_COUNT; li++) {
      const xOrigin = (li - (LETTERS_COUNT - 1) / 2) * 1.55;
      for (let s = 0; s < SHARDS_PER_LETTER; s++) {
        const angle = (s / SHARDS_PER_LETTER) * Math.PI * 2 + li * 0.4;
        shardData.push({
          x0: xOrigin + Math.cos(angle) * 0.45,
          y0: Math.sin(angle) * 0.55,
          z0: 0,
          dx: Math.cos(angle) * (2.5 + Math.random() * 3.5),
          dy: Math.sin(angle) * (1.5 + Math.random() * 2.8),
          dz: -1.5 - Math.random() * 5,
          rx: Math.random() * Math.PI * 2,
          ry: Math.random() * Math.PI * 2,
          spinX: (Math.random() - 0.5) * 5,
          spinY: (Math.random() - 0.5) * 5,
          size: 0.18 + Math.random() * 0.25,
          colorIdx: li * SHARDS_PER_LETTER + s,
        });
      }
    }
    const shardGeo = new THREE.TetrahedronGeometry(1, 0);
    const shardMat = new THREE.MeshStandardMaterial({
      metalness: 0.5,
      roughness: 0.25,
      emissive: new THREE.Color(PALETTE.primary),
      emissiveIntensity: 0.4,
    });
    const shards = new THREE.InstancedMesh(shardGeo, shardMat, TOTAL_SHARDS);
    shards.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(TOTAL_SHARDS * 3), 3);
    shards.visible = false;
    scene.add(shards);
    refs.shards = shards;
    refs.shardData = shardData;

    // --- Galaxy (150 plane sprites on a fibonacci sphere) ---
    const galaxyData = [];
    for (let i = 0; i < GALAXY_COUNT; i++) {
      galaxyData.push({
        phi: Math.acos(1 - 2 * (i + 0.5) / GALAXY_COUNT),
        theta: Math.PI * (1 + Math.sqrt(5)) * i,
        radius: 4.5 + Math.random() * 3.5,
        spin: 0.05 + Math.random() * 0.18,
        wobble: Math.random() * Math.PI * 2,
        size: 0.32 + Math.random() * 0.55,
        color: new THREE.Color(colorForIndex(i)),
        offsetTheta: Math.random() * Math.PI * 2,
      });
    }
    const galaxyGeo = new THREE.PlaneGeometry(1, 1.4);
    const galaxyMat = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      metalness: 0.7,
      roughness: 0.25,
      emissive: new THREE.Color(PALETTE.primary),
      emissiveIntensity: 0.55,
      transparent: true,
      opacity: 0.92,
    });
    const galaxy = new THREE.InstancedMesh(galaxyGeo, galaxyMat, GALAXY_COUNT);
    galaxy.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(GALAXY_COUNT * 3), 3);
    galaxy.visible = false;
    scene.add(galaxy);
    refs.galaxy = galaxy;
    refs.galaxyData = galaxyData;

    // --- Dust field ---
    const dustData = [];
    for (let i = 0; i < DUST_COUNT; i++) {
      dustData.push({
        x: (Math.random() - 0.5) * 24,
        y: (Math.random() - 0.5) * 16,
        z: (Math.random() - 0.5) * 16 - 2,
        size: 0.025 + Math.random() * 0.05,
        speed: 0.04 + Math.random() * 0.15,
        seed: Math.random() * 1000,
      });
    }
    const dustGeo = new THREE.SphereGeometry(1, 6, 6);
    const dustMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(PALETTE.primary),
      transparent: true,
      opacity: 0.6,
    });
    const dust = new THREE.InstancedMesh(dustGeo, dustMat, DUST_COUNT);
    scene.add(dust);
    refs.dust = dust;
    refs.dustData = dustData;

    return () => {
      scene.remove(ambient, dir, p1, p2, shards, galaxy, dust);
      shardGeo.dispose(); shardMat.dispose();
      galaxyGeo.dispose(); galaxyMat.dispose();
      dustGeo.dispose(); dustMat.dispose();
    };
  }, [scene, refs]);

  // ---------- Per-frame animation ----------
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    const p = progress.current;
    const mx = mouse.x.current;
    const my = mouse.y.current;
    const { shards, galaxy, dust, shardData, galaxyData, dustData, dummy, lookTarget, cameraTarget } = refs;

    // Camera dolly + parallax
    cameraTarget.set(mx * 0.6, my * 0.4, 14 - smooth(Math.min(1, p)) * 10);
    camera.position.lerp(cameraTarget, 0.08);
    camera.lookAt(0, 0, 0);

    // Shards
    if (shards) {
      const t = remap(p, TIMELINE.shatterStart, TIMELINE.shatterEnd);
      const eased = smooth(t);
      shards.visible = eased > 0.002 && eased < 0.99;
      if (shards.visible) {
        for (let i = 0; i < TOTAL_SHARDS; i++) {
          const sh = shardData[i];
          dummy.position.set(
            sh.x0 + sh.dx * eased + Math.sin(time * 0.5 + i) * 0.06 * eased,
            sh.y0 + sh.dy * eased - 0.8 * eased * eased,
            sh.z0 + sh.dz * eased,
          );
          dummy.rotation.set(
            sh.rx + sh.spinX * eased * 2,
            sh.ry + sh.spinY * eased * 2,
            eased * 2,
          );
          const s = Math.max(0.001, sh.size * Math.max(0, 1 - eased * 0.4));
          dummy.scale.setScalar(s);
          dummy.updateMatrix();
          shards.setMatrixAt(i, dummy.matrix);
          const c = new THREE.Color(colorForIndex(sh.colorIdx));
          c.lerp(new THREE.Color('#ffffff'), 1 - eased);
          shards.setColorAt(i, c);
        }
        shards.instanceMatrix.needsUpdate = true;
        if (shards.instanceColor) shards.instanceColor.needsUpdate = true;
      }
    }

    // Galaxy
    if (galaxy) {
      const gT = remap(p, TIMELINE.galaxyStart, TIMELINE.galaxyEnd);
      const eased = smooth(gT);
      galaxy.visible = eased > 0.005;
      if (galaxy.visible) {
        const globalSpin = time * 0.08 + mx * 0.4;
        for (let i = 0; i < GALAXY_COUNT; i++) {
          const g = galaxyData[i];
          const r = g.radius * (0.55 + eased * 0.45);
          const theta = g.theta + g.offsetTheta + globalSpin * g.spin;
          const phi = g.phi + Math.sin(time * 0.3 + g.wobble) * 0.04;
          dummy.position.set(
            r * Math.sin(phi) * Math.cos(theta),
            r * Math.sin(phi) * Math.sin(theta) + my * 0.5,
            r * Math.cos(phi) - 2 + eased * 1.5,
          );
          dummy.lookAt(lookTarget);
          const scale = g.size * eased;
          dummy.scale.set(scale, scale * 1.4, scale);
          dummy.updateMatrix();
          galaxy.setMatrixAt(i, dummy.matrix);
          galaxy.setColorAt(i, g.color);
        }
        galaxy.instanceMatrix.needsUpdate = true;
        if (galaxy.instanceColor) galaxy.instanceColor.needsUpdate = true;
      }
    }

    // Dust (always on)
    if (dust) {
      for (let i = 0; i < DUST_COUNT; i++) {
        const d = dustData[i];
        dummy.position.set(
          d.x + Math.sin(time * d.speed + d.seed) * 0.5,
          d.y + Math.cos(time * d.speed + d.seed) * 0.4,
          d.z,
        );
        dummy.scale.setScalar(d.size);
        dummy.updateMatrix();
        dust.setMatrixAt(i, dummy.matrix);
      }
      dust.instanceMatrix.needsUpdate = true;
    }
  });

  return null;
}
